<template>
  <Container as="section" data-component="NeighbourhoodMap">
    <Grid>
      <div class="col-span-12">
        <header class="mb-8 lg:mb-10">
          <Typography as="h2" variant="header-3">{{ header }}</Typography>
          <p v-if="description" class="text-primary-60 mt-2" v-text="description" />
        </header>
      </div>
    </Grid>
    <Grid>
      <div class="col-span-12 lg:col-span-4 order-2 lg:order-1">
        <ul class="border-y border-primary-20 divide-y divide-primary-20">
          <li
            v-for="loc in filteredMapLocations"
            :key="loc.id"
            class="py-3"
            @mouseenter="hoveredLocationId = loc.id"
            @mouseleave="hoveredLocationId = null">
            <a
              :href="googleMapsUrlFromLocation(loc)"
              target="_blank"
              rel="noopener"
              class="flex items-center justify-between gap-3 py-2 md:py-3 px-1 md:px-2 rounded-md hover:text-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-40 group">
              <div class="min-w-0 text-primary-100">
                <span class="block">
                  <Typography as="span" variant="location-card-header" class="underline-hover-effect-group">
                    {{ loc.title }}
                  </Typography>
                </span>
                <span v-if="loc.address" class="block text-body">{{ loc.address }}</span>
              </div>
              <SvgComponent
                icon="icon-external-link"
                class="w-[19px] h-[19px] shrink-0 text-primary-60"
                aria-hidden="true" />
            </a>
          </li>
        </ul>
      </div>
      <div
        class="col-span-12 lg:col-span-8 order-1 lg:order-2 h-[55svh] lg:h-auto lg:aspect-[3/2] lg:sticky lg:top-8 lg:self-start rounded-lg overflow-hidden bg-theme-30">
        <Map
          :access-token="mapboxAccessToken"
          :initial-center="initialCenter"
          :initial-zoom="9"
          :locations="mapLocations"
          :hovered-location-id="hoveredLocationId"
          @init="onMapInit"
          @moveend="onMapMoveEnd" />
      </div>
    </Grid>
  </Container>
</template>

<script setup lang="ts">
import type { Location } from '@corporate/components/LocationCard/LocationCard.vue'
import Map from '@corporate/components/Map/Map.vue'
import { isLocationWithinBounds } from '@corporate/utils/location'
import type { NeighbourhoodMapGridApiModel } from '@gro-up/api/corporate'
import type { Domain } from '@gro-up/types'
import { SvgComponent } from '@gro-up/ui'
import { Container, Grid, Typography } from '@gro-up/ui'
import type { LngLatBounds, Map as MapboxMap } from 'mapbox-gl'
import { useRuntimeConfig } from 'nuxt/app'
import { computed, ref } from 'vue'

const props = defineProps<NeighbourhoodMapGridApiModel>()

const runtimeConfig = useRuntimeConfig()
const mapboxAccessToken = runtimeConfig.public.mapboxAccessToken as string

const googleMapsUrlFromLocation = (loc: Pick<Location, 'position'>) => {
  const { latitude, longitude } = loc.position
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${latitude},${longitude}`)}`
}

const mapLocations = computed<Location[]>(() => {
  const defaultDomain = 'head-office' as Domain
  return (props.locations ?? [])
    .filter(l => l.coordinates?.latitude != null && l.coordinates?.longitude != null)
    .map((l, idx) => {
      const position = {
        latitude: l.coordinates?.latitude ?? 0,
        longitude: l.coordinates?.longitude ?? 0,
      }

      // Without a CMS link the popup falls back to a route in Google Maps
      return {
        id: `${idx}`,
        title: l.title,
        address: l.address,
        domain: l.domain ?? defaultDomain,
        image: null,
        url: l.link?.url || googleMapsUrlFromLocation({ position }),
        linkLabel: l.link?.title || (l.link?.url ? 'Bekijk deze locatie' : 'Plan je route'),
        position,
      }
    })
})

const initialCenter = computed<[number, number]>(() => {
  const first = mapLocations.value[0]
  return first ? [first.position.longitude, first.position.latitude] : [4.6, 52]
})

// expose header/description for template
const header = computed(() => props.header)
const description = computed(() => props.description ?? '')

const hoveredLocationId = ref<string | null>(null)
const mapBounds = ref<LngLatBounds | null>(null)

const onMapInit = (mapboxMap: MapboxMap) => {
  mapBounds.value = mapboxMap.getBounds()
}

const onMapMoveEnd = (map: MapboxMap) => {
  mapBounds.value = map.getBounds()
}

const filteredMapLocations = computed(() => {
  if (!mapBounds.value) {
    return mapLocations.value
  }
  return mapLocations.value.filter(location => isLocationWithinBounds(location, mapBounds.value!))
})
</script>
